import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, AutoComplete,
  DatePicker, Tag, Space, Typography, Badge, message, Tooltip, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, SwapOutlined, CheckCircleOutlined,
  ClockCircleOutlined, DollarOutlined, LaptopOutlined, StopOutlined,
  InfoCircleOutlined, TeamOutlined, ReloadOutlined, DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { safeFormatTimestamp } from '../../utils/timezone';
import logger from '../../services/logger';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

const DeviceRentals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [endModalVisible, setEndModalVisible] = useState(false);
  const [editingRental, setEditingRental] = useState(null);
  const [endingRental, setEndingRental] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [form] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [endForm] = Form.useForm();
  const [billingType, setBillingType] = useState('fixed_monthly');

  // Device/Customer search
  const [deviceSearchText, setDeviceSearchText] = useState('');
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);

  const fetchRentals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (showActiveOnly) params.active_only = true;
      const response = await axios.get('/api/v1/rentals', { params });
      setRentals(response.data);
    } catch (error) {
      logger.error('Kiralamalar yüklenirken hata:', error);
      message.error('Kiralamalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Müşteriler yüklenirken hata:', error);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/devices');
      setDevices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Cihazlar yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
    fetchCustomers();
    fetchDevices();
  }, [fetchRentals, fetchCustomers, fetchDevices]);

  // Device search
  useEffect(() => {
    if (deviceSearchText && deviceSearchText.length >= 1) {
      const filtered = devices.filter(d =>
        d.name.toLowerCase().includes(deviceSearchText.toLowerCase()) ||
        d.mac_address.toLowerCase().includes(deviceSearchText.toLowerCase())
      ).map(d => ({ value: d.id.toString(), label: `${d.name} (${d.mac_address})` }));
      setDeviceOptions(filtered);
    } else {
      setDeviceOptions(devices.map(d => ({ value: d.id.toString(), label: `${d.name} (${d.mac_address})` })));
    }
  }, [deviceSearchText, devices]);

  // Customer search
  useEffect(() => {
    if (customerSearchText && customerSearchText.length >= 1) {
      const filtered = customers.filter(c =>
        c.name.toLowerCase().includes(customerSearchText.toLowerCase())
      ).map(c => ({ value: c.id.toString(), label: c.name }));
      setCustomerOptions(filtered);
    } else {
      setCustomerOptions(customers.map(c => ({ value: c.id.toString(), label: c.name })));
    }
  }, [customerSearchText, customers]);

  // === CRUD Handlers ===
  const handleCreate = () => {
    setEditingRental(null);
    form.resetFields();
    form.setFieldsValue({ billing_type: 'fixed_monthly', currency: 'TRY', start_date: dayjs() });
    setBillingType('fixed_monthly');
    setDeviceSearchText('');
    setCustomerSearchText('');
    setModalVisible(true);
  };

  const handleEdit = (rental) => {
    setEditingRental(rental);
    form.setFieldsValue({
      monthly_rate: rental.monthly_rate,
      unit_price: rental.unit_price,
      notes: rental.notes,
      end_date: rental.end_date ? dayjs(rental.end_date) : null,
    });
    setBillingType(rental.billing_type);
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingRental) {
        await axios.put(`/api/v1/rentals/${editingRental.id}`, {
          monthly_rate: values.monthly_rate,
          unit_price: values.unit_price,
          notes: values.notes,
          end_date: values.end_date ? values.end_date.toISOString() : null,
        });
        message.success('Kiralama güncellendi');
      } else {
        const data = {
          device_id: parseInt(values.device_id),
          customer_id: parseInt(values.customer_id),
          start_date: values.start_date.toISOString(),
          end_date: values.end_date ? values.end_date.toISOString() : null,
          billing_type: values.billing_type,
          monthly_rate: values.monthly_rate,
          unit_price: values.unit_price,
          currency: values.currency || 'TRY',
          notes: values.notes,
        };
        await axios.post('/api/v1/rentals', data);
        message.success('Kiralama oluşturuldu');
      }

      setModalVisible(false);
      form.resetFields();
      fetchRentals();
      fetchDevices();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Kiralama kaydedilirken hata oluştu');
    }
  };

  // End rental with custom date
  const handleShowEndModal = (rental) => {
    setEndingRental(rental);
    endForm.setFieldsValue({ end_date: dayjs() });
    setEndModalVisible(true);
  };

  const handleEndRental = async () => {
    try {
      const values = await endForm.validateFields();
      const endDate = values.end_date.toISOString();
      await axios.post(`/api/v1/rentals/${endingRental.id}/end?end_date=${encodeURIComponent(endDate)}`);
      message.success('Kiralama sonlandırıldı');
      setEndModalVisible(false);
      fetchRentals();
      fetchDevices();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Kiralama sonlandırılırken hata oluştu');
    }
  };

  // Delete rental
  const handleDeleteRental = (rental) => {
    Modal.confirm({
      title: 'Kiralama Sil',
      content: `Bu kiralama kaydını silmek istediğinize emin misiniz? (ID: ${rental.id})`,
      okText: 'Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/rentals/${rental.id}`);
          message.success('Kiralama silindi');
          fetchRentals();
          fetchDevices();
        } catch (error) {
          message.error(error.response?.data?.detail || 'Kiralama silinirken hata oluştu');
        }
      },
    });
  };

  // Transfer with pre-filled values
  const handleTransfer = (rental) => {
    transferForm.resetFields();
    transferForm.setFieldsValue({
      device_id: rental.device_id,
      billing_type: rental.billing_type,
      monthly_rate: rental.monthly_rate,
      unit_price: rental.unit_price,
      transfer_date: dayjs(),
    });
    setBillingType(rental.billing_type);
    setCustomerSearchText('');
    setTransferModalVisible(true);
  };

  const handleTransferSubmit = async () => {
    try {
      const values = await transferForm.validateFields();
      await axios.post('/api/v1/rentals/transfer', {
        device_id: values.device_id,
        new_customer_id: parseInt(values.new_customer_id),
        transfer_date: values.transfer_date.toISOString(),
        billing_type: values.billing_type,
        monthly_rate: values.monthly_rate,
        unit_price: values.unit_price,
        notes: values.notes,
      });
      message.success('Cihaz transfer edildi');
      setTransferModalVisible(false);
      fetchRentals();
      fetchDevices();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Transfer sırasında hata oluştu');
    }
  };

  // Duration helper
  const getDuration = (startDate) => {
    if (!startDate) return '-';
    const days = dayjs().diff(dayjs(startDate), 'day');
    if (days < 30) return `${days} gün`;
    const months = Math.floor(days / 30);
    const remainDays = days % 30;
    return remainDays > 0 ? `${months} ay ${remainDays} gün` : `${months} ay`;
  };

  // Stats
  const activeRentals = rentals.filter(r => r.is_active).length;
  const fixedMonthlyRentals = rentals.filter(r => r.billing_type === 'fixed_monthly' && r.is_active).length;
  const perShotRentals = rentals.filter(r => r.billing_type === 'per_shot' && r.is_active).length;

  const columns = [
    {
      title: 'Cihaz', dataIndex: 'device_name', key: 'device_name', width: 150,
      render: (text, record) => (
        <a onClick={() => navigate(`/devices/${record.device_id}`)}>{text || `#${record.device_id}`}</a>
      ),
    },
    {
      title: 'Müşteri', dataIndex: 'customer_name', key: 'customer_name', width: 140,
      render: (text, record) => (
        <a onClick={() => navigate(`/customers/${record.customer_id}`)}>{text || `#${record.customer_id}`}</a>
      ),
    },
    {
      title: 'Başlangıç', dataIndex: 'start_date', key: 'start_date', width: 150,
      render: (date) => date ? safeFormatTimestamp(date) : '-',
    },
    {
      title: 'Bitiş', dataIndex: 'end_date', key: 'end_date', width: 150,
      render: (date) => date ? safeFormatTimestamp(date) : <Tag color="green">Devam ediyor</Tag>,
    },
    {
      title: 'Süre', key: 'duration', width: 100,
      render: (_, record) => record.is_active ? getDuration(record.start_date) : '-',
    },
    {
      title: 'Tip', dataIndex: 'billing_type', key: 'billing_type', width: 110,
      render: (type) => <Tag color={type === 'fixed_monthly' ? 'blue' : 'purple'}>{type === 'fixed_monthly' ? 'Sabit Aylık' : 'Atış Bazlı'}</Tag>,
    },
    {
      title: 'Ücret', key: 'rate', width: 130,
      render: (_, r) => r.billing_type === 'fixed_monthly'
        ? (r.monthly_rate ? `₺${parseFloat(r.monthly_rate).toFixed(2)}/ay` : '-')
        : (r.unit_price ? `₺${parseFloat(r.unit_price).toFixed(4)}/atış` : '-'),
    },
    {
      title: 'Not', dataIndex: 'notes', key: 'notes', width: 100, ellipsis: true,
      render: (text) => text ? <Tooltip title={text}><Text ellipsis style={{ maxWidth: 80 }}>{text}</Text></Tooltip> : '-',
    },
    {
      title: 'İşlemler', key: 'actions', width: 150, align: 'center',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Düzenle">
            <Button icon={<EditOutlined />} size="small" type="text" onClick={() => handleEdit(record)} />
          </Tooltip>
          {record.is_active && (
            <>
              <Tooltip title="Transfer">
                <Button icon={<SwapOutlined />} size="small" type="text" onClick={() => handleTransfer(record)} />
              </Tooltip>
              <Tooltip title="Sonlandır">
                <Button icon={<StopOutlined />} size="small" type="text" danger onClick={() => handleShowEndModal(record)} />
              </Tooltip>
            </>
          )}
          <Tooltip title="Sil">
            <Button icon={<DeleteOutlined />} size="small" type="text" danger onClick={() => handleDeleteRental(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const getFilteredRentals = () => {
    if (!searchText) return rentals;
    const s = searchText.toLowerCase();
    return rentals.filter(r =>
      r.device_name?.toLowerCase().includes(s) ||
      r.device_mac?.toLowerCase().includes(s) ||
      r.customer_name?.toLowerCase().includes(s) ||
      r.billing_type?.toLowerCase().includes(s) ||
      r.status?.toLowerCase().includes(s)
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <LaptopOutlined /> Cihaz Kiralamaları
        </Title>
      </div>

      {/* Stats */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Space size="large" wrap>
            <span>
              <LaptopOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam</Text>
              <Text strong style={{ fontSize: 18 }}>{rentals.length}</Text>
            </span>
            <span>
              <Badge status="success" />
              <Text style={{ marginRight: 4 }}>Aktif</Text>
              <Text strong style={{ color: '#3f8600', fontSize: 18 }}>{activeRentals}</Text>
            </span>
            <span>
              <DollarOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Sabit Aylık</Text>
              <Text strong style={{ fontSize: 16 }}>{fixedMonthlyRentals}</Text>
            </span>
            <span>
              <DollarOutlined style={{ color: '#722ed1', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Atış Bazlı</Text>
              <Text strong style={{ fontSize: 16 }}>{perShotRentals}</Text>
            </span>
          </Space>
          <Space>
            <Text type="secondary">Sadece aktif</Text>
            <Switch checked={showActiveOnly} onChange={(v) => setShowActiveOnly(v)} size="small" />
          </Space>
        </div>
      </Card>

      {/* Action Buttons + Search */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Yeni Kiralama</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchRentals(); fetchDevices(); }}>Yenile</Button>
          </Space>
          <div style={{ minWidth: 400 }}>
            <Input.Search
              placeholder="Cihaz, müşteri, durum ara..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={getFilteredRentals()}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Toplam ${t} kiralama` }}
          size="small"
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingRental ? 'Kiralama Düzenle' : 'Yeni Kiralama'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText={editingRental ? 'Güncelle' : 'Oluştur'}
        width={600}
      >
        <Form form={form} layout="vertical">
          {!editingRental && (
            <>
              <Form.Item name="device_id" label="Cihaz" rules={[{ required: true, message: 'Cihaz seçin' }]}>
                <Select
                  showSearch
                  placeholder="Cihaz ara..."
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {devices.map(d => (
                    <Option key={d.id} value={d.id}>{d.name} ({d.mac_address})</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="customer_id" label="Müşteri" rules={[{ required: true, message: 'Müşteri seçin' }]}>
                <Select
                  showSearch
                  placeholder="Müşteri ara..."
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {customers.map(c => (
                    <Option key={c.id} value={c.id}>{c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="start_date" label="Başlangıç Tarihi ve Saati" rules={[{ required: true, message: 'Başlangıç tarihi seçin' }]}>
                <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="end_date" label="Bitiş Tarihi (Opsiyonel)">
                <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="billing_type" label="Faturalama Tipi" rules={[{ required: true }]}>
                <Select onChange={(v) => setBillingType(v)}>
                  <Option value="fixed_monthly">Sabit Aylık</Option>
                  <Option value="per_shot">Atış Bazlı</Option>
                </Select>
              </Form.Item>
            </>
          )}
          {billingType === 'fixed_monthly' && (
            <Form.Item name="monthly_rate" label="Aylık Ücret (₺)" rules={editingRental ? [] : [{ required: true, message: 'Aylık ücret girin' }]}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          )}
          {billingType === 'per_shot' && (
            <Form.Item name="unit_price" label="Atış Başına Ücret (₺)" rules={editingRental ? [] : [{ required: true, message: 'Birim fiyat girin' }]}>
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="Notlar">
            <TextArea rows={2} placeholder="Kiralama ile ilgili notlar..." />
          </Form.Item>
          {!editingRental && (
            <Form.Item name="currency" label="Para Birimi" hidden>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* End Rental Modal */}
      <Modal
        title="Kiralama Sonlandır"
        open={endModalVisible}
        onCancel={() => setEndModalVisible(false)}
        onOk={handleEndRental}
        okText="Sonlandır"
        okButtonProps={{ danger: true }}
        width={500}
      >
        {endingRental && (
          <div style={{ marginBottom: 16 }}>
            <Card size="small" style={{ background: '#fafafa' }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <div><Text type="secondary">Cihaz:</Text> <Text strong>{endingRental.device_name}</Text></div>
                <div><Text type="secondary">Müşteri:</Text> <Text strong>{endingRental.customer_name}</Text></div>
                <div><Text type="secondary">Başlangıç:</Text> <Text>{safeFormatTimestamp(endingRental.start_date)}</Text></div>
                <div><Text type="secondary">Tip:</Text> <Tag color={endingRental.billing_type === 'fixed_monthly' ? 'blue' : 'purple'}>{endingRental.billing_type === 'fixed_monthly' ? 'Sabit Aylık' : 'Atış Bazlı'}</Tag></div>
              </Space>
            </Card>
          </div>
        )}
        <Form form={endForm} layout="vertical">
          <Form.Item name="end_date" label="Bitiş Tarihi ve Saati" rules={[{ required: true }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Transfer Modal */}
      <Modal
        title="Cihaz Transfer"
        open={transferModalVisible}
        onCancel={() => setTransferModalVisible(false)}
        onOk={handleTransferSubmit}
        okText="Transfer Et"
        width={600}
      >
        <Card size="small" style={{ background: '#fff7e6', marginBottom: 16 }}>
          <InfoCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
          <Text>Mevcut kiralama otomatik sonlandırılır ve yeni müşteri için yeni kiralama başlatılır.</Text>
        </Card>
        <Form form={transferForm} layout="vertical">
          <Form.Item name="device_id" label="Cihaz" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="new_customer_id" label="Yeni Müşteri" rules={[{ required: true, message: 'Müşteri seçin' }]}>
            <AutoComplete
              placeholder="Müşteri ara..."
              options={customerOptions}
              onSearch={setCustomerSearchText}
              filterOption={false}
            />
          </Form.Item>
          <Form.Item name="transfer_date" label="Transfer Tarihi ve Saati" rules={[{ required: true }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="billing_type" label="Faturalama Tipi" rules={[{ required: true }]}>
            <Select onChange={(v) => setBillingType(v)}>
              <Option value="fixed_monthly">Sabit Aylık</Option>
              <Option value="per_shot">Atış Bazlı</Option>
            </Select>
          </Form.Item>
          {billingType === 'fixed_monthly' && (
            <Form.Item name="monthly_rate" label="Aylık Ücret (₺)">
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          )}
          {billingType === 'per_shot' && (
            <Form.Item name="unit_price" label="Atış Başına Ücret (₺)">
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="Transfer Notu">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceRentals;
